import { useSelector } from "react-redux";
import React from "react";
import { Tooltip } from "@mui/material";

import makeStyles from "@mui/styles/makeStyles";

import { palette } from "../../theme/palette";

import { routeHasTerritory } from "../../utils/routes";

import {
  getSelectedPlanDay,
  routeDayLockSelector,
} from "../../reducers/matrixProfile/selectors";

import { View } from "../View";
import { LockIcon } from "../Icons";
import { FULL_DAYS } from "../../consts/strings";

const useStyles = makeStyles(() => ({
  container: {
    display: "flex",
    maxHeight: "1.25rem",
    marginBottom: "0.6rem",
    borderRadius: "0rem 0.5rem 0.5rem 0rem",
    backgroundColor: ({ routeDayLock }) => {
      if (routeDayLock?.changed) return palette.lockOrange;
      else if (
        routeDayLock?.lockedByYou &&
        !routeDayLock.lockedInDifferentBrowser
      )
        return palette.lockGreen;
      else if (routeDayLock?.lockedByUserCode) return palette.lockRed;
      else return palette.transparent;
    },
    marginRight: ({ hasLockIcon }) => (hasLockIcon ? "-1rem" : "0rem"),
  },
  lockIcon: {
    color: palette.typography.black.solid,
    margin: "0.2rem 0.25rem 0rem 0.25rem",
    width: "10px",
    height: "12px",
  },
  routeCode: {
    width: "2.5rem",
    height: "1.25rem",
    lineHeight: "1.25rem",
    fontSize: "0.75rem",
    textAlign: "center",
    borderRadius: "0.125rem",
    fontWeight: "bold",
    background: "green",
    color: "white",
    marginBottom: "9px",
    cursor: "pointer",
    position: "relative",
  },
  shape: {
    position: "absolute",
    width: "0.25rem",
    height: "0.25rem",
    borderWidth: "0 0.094rem 0.094rem 0",
    borderColor: "white",
    borderStyle: "solid",
    bottom: "0.125rem",
    right: "0.125rem",
  },
}));

const RouteLabel = ({ route, planType, collectionType, onClick, ...rest }) => {
  const dayOfWeek = useSelector(getSelectedPlanDay);
  const routeDayLock = useSelector(routeDayLockSelector(route.routeCode));
  const hasLockIcon = routeDayLock?.changed || routeDayLock?.lockedByUserCode;
  const styles = useStyles({
    routeDayLock,
    hasLockIcon,
  });
  const hasTerritory = routeHasTerritory(route, planType, collectionType);

  const getRouteLockTooltipText = () => {
    if (!routeDayLock) return null;
    else if (routeDayLock.changed)
      return `Route ${route.routeCode} ${FULL_DAYS[dayOfWeek]} has been changed.`;
    else if (routeDayLock.lockedInDifferentBrowser)
      return `Route ${route.routeCode} ${FULL_DAYS[dayOfWeek]} is locked by you in different browser/application.`;
    else if (routeDayLock.lockedByYou)
      return `Route ${route.routeCode} ${FULL_DAYS[dayOfWeek]} is locked by you.`;
    else if (routeDayLock.lockedByUserCode)
      return `Route ${route.routeCode} ${FULL_DAYS[dayOfWeek]} is locked by ${routeDayLock.lockedByUserCode} ${routeDayLock.lockedByUserName}.`;
    else return ``;
  };

  return (
    <Tooltip
      title={getRouteLockTooltipText()}
      enterDelay={500}
      enterNextDelay={500}
    >
      <View className={styles.container}>
        <View
          style={{
            background: hasTerritory ? route.colour : palette.translucentBlack,
            color: hasTerritory
              ? route?.textColour || palette.typography.white.high
              : palette.typography.white.high,
            display: "inline-block",
          }}
          className={styles.routeCode}
          onClick={(e) => {
            e.stopPropagation();
            onClick && onClick();
          }}
          {...rest}
        >
          {route.routeCode}
          <View className={styles.shape} />
        </View>
        {hasLockIcon && <LockIcon className={styles.lockIcon} />}
      </View>
    </Tooltip>
  );
};

export default RouteLabel;
